import type { AuditListItem } from '@clockwise/clients/audit';
import { entityTypeLabel } from '@clockwise/shared';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { AuditActor, DiffTable, OperationBadge } from './audit-diff';

/**
 * Single-object audit history: a compact expandable list for a record's audit
 * tab (customers, SOWs, products, rate cards). The platform-wide screen uses
 * the richer table {@link AuditLogView} instead; both render the same diff and
 * actor via the shared `audit-diff` helpers so they never drift.
 *
 * Pass `resolveLabel` to translate `customFields.<uuid>` field keys to their
 * human label (via the host's `useResolveCustomFieldLabel`).
 */
export function AuditEntryList({
  items,
  total,
  resolveLabel,
  showObjectType = false,
}: {
  items: AuditListItem[];
  total: number;
  resolveLabel?: (field: string, fallback: string) => string;
  /**
   * Roll-up mode: the list mixes multiple entity types (a customer's own
   * changes plus its team/contacts/SOWs, or an employee plus their cost). Show
   * a per-row type chip so each entry is attributable. Off for single-object
   * tabs where every row is the same type.
   */
  showObjectType?: boolean;
}) {
  if (items.length === 0) {
    return <p className="p-4 text-sm text-muted-foreground">No audit entries yet.</p>;
  }
  return (
    <ul className="divide-y">
      {items.map((entry) => (
        <AuditEntryRow
          key={entry.id}
          entry={entry}
          resolveLabel={resolveLabel}
          showObjectType={showObjectType}
        />
      ))}
      {total > items.length ? (
        <li className="p-3 text-center text-xs text-muted-foreground">
          Showing first {items.length} of {total}.
        </li>
      ) : null}
    </ul>
  );
}

function AuditEntryRow({
  entry,
  resolveLabel,
  showObjectType,
}: {
  entry: AuditListItem;
  resolveLabel?: (field: string, fallback: string) => string;
  showObjectType?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <li className="px-3 py-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-3 text-left"
      >
        {open ? <ChevronDown className="shrink-0" /> : <ChevronRight className="shrink-0" />}
        <OperationBadge operation={entry.operation} />
        {showObjectType ? (
          <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
            {entityTypeLabel(entry.entityType)}
          </span>
        ) : null}
        <span className="text-sm">
          {/* Roll-up rows mix entities (e.g. a customer's team members), so lead
              with the affected row's primary field — its resolved name — to make
              each entry identifiable without expanding. */}
          {showObjectType && entry.label ? (
            <span className="font-semibold">{entry.label} · </span>
          ) : null}
          {entry.summary}
        </span>
        {/* Fixed right column: shrink-0 + whitespace-nowrap anchors it to the
            right edge and keeps the timestamp/actor on one line (no mid-phrase
            wrap), so it lines up row-to-row; the summary is the part that wraps.
            items-start (above) top-aligns it with a multi-line summary. */}
        <span className="ml-auto shrink-0 whitespace-nowrap text-right text-xs text-muted-foreground">
          {new Date(entry.occurredAt).toLocaleString()} · <AuditActor entry={entry} />
        </span>
      </button>
      {open ? (
        <DiffTable
          changes={entry.changes}
          resolveLabel={resolveLabel}
          operation={entry.operation}
        />
      ) : null}
    </li>
  );
}
